import React from 'react'
import { Button, Heading, Input } from '../ui'
import { hasAdmNotaDownloadKey } from '../../utils/admDespesasStorageDownload'
import type { AdmOrderHeader } from '../../types/admDespesas'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'

export type BudgetEditModalState = {
  id: string
  documentKind: string
  number: string
  issueDate: string
  issuerName: string
  issuerCnpj: string
  recipientName: string
  recipientDocument: string
  totalProducts: string
  totalFreight: string
  totalDiscount: string
  totalValue: string
  paymentMethod: string
  paymentDate: string
  manualNotes: string
  fileName: string
  fileNameStorage: string
}

export function budgetEditStateFromRecord(h: AdmOrderHeader): BudgetEditModalState {
  return {
    id: h.id,
    documentKind: h.document_kind ?? '',
    number: h.number ?? '',
    issueDate: h.issue_date ? h.issue_date.slice(0, 10) : '',
    issuerName: h.issuer_name ?? '',
    issuerCnpj: h.issuer_cnpj ?? '',
    recipientName: h.recipient_name ?? '',
    recipientDocument: h.recipient_document ?? '',
    totalProducts: h.total_products != null ? String(h.total_products) : '',
    totalFreight: h.total_freight != null ? String(h.total_freight) : '',
    totalDiscount: h.total_discount != null ? String(h.total_discount) : '',
    totalValue: h.total_value != null ? String(h.total_value) : '',
    paymentMethod: h.payment_method ?? '',
    paymentDate: h.payment_date ? h.payment_date.slice(0, 10) : '',
    manualNotes: h.manual_notes ?? '',
    fileName: h.file_name ?? '',
    fileNameStorage: h.file_name_storage ?? '',
  }
}

export function budgetEditPatchFromModal(modal: BudgetEditModalState) {
  return {
    document_kind: modal.documentKind.trim() || null,
    number: modal.number.trim() || null,
    issue_date: modal.issueDate.trim() || null,
    issuer_name: modal.issuerName.trim() || null,
    issuer_cnpj: modal.issuerCnpj.trim() || null,
    recipient_name: modal.recipientName.trim() || null,
    recipient_document: modal.recipientDocument.trim() || null,
    total_products: modal.totalProducts.trim() ? Number(modal.totalProducts) : null,
    total_freight: modal.totalFreight.trim() ? Number(modal.totalFreight) : null,
    total_discount: modal.totalDiscount.trim() ? Number(modal.totalDiscount) : null,
    total_value: modal.totalValue.trim() ? Number(modal.totalValue) : null,
    payment_method: modal.paymentMethod.trim() || null,
    payment_date: modal.paymentDate.trim() || null,
    manual_notes: modal.manualNotes.trim() || null,
    file_name: modal.fileName.trim() || null,
    file_name_storage: modal.fileNameStorage.trim() || null,
  }
}

type AdmDespesasBudgetEditModalProps = {
  modal: BudgetEditModalState
  submitting: boolean
  downloadBusy?: boolean
  downloadError?: string | null
  onChange: (updater: (prev: BudgetEditModalState) => BudgetEditModalState) => void
  onClose: () => void
  onSave: () => void
  onDownload?: () => void
}

const AdmDespesasBudgetEditModal: React.FC<AdmDespesasBudgetEditModalProps> = ({
  modal,
  submitting,
  downloadBusy = false,
  downloadError = null,
  onChange,
  onClose,
  onSave,
  onDownload,
}) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
        Editar orçamento / pedido
      </Heading>
      <div className="space-y-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Tipo (document_kind)</label>
          <Input
            value={modal.documentKind}
            onChange={(e) => onChange((p) => ({ ...p, documentKind: e.target.value }))}
            placeholder="orcamento ou pedido"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Número</label>
          <Input
            value={modal.number}
            onChange={(e) => onChange((p) => ({ ...p, number: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Data emissão</label>
          <Input
            type="date"
            value={modal.issueDate}
            onChange={(e) => onChange((p) => ({ ...p, issueDate: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Meio de pagamento</label>
          <Input
            value={modal.paymentMethod}
            onChange={(e) => onChange((p) => ({ ...p, paymentMethod: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Emitente</label>
          <Input
            value={modal.issuerName}
            onChange={(e) => onChange((p) => ({ ...p, issuerName: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">CNPJ emitente</label>
          <Input
            value={modal.issuerCnpj}
            onChange={(e) => onChange((p) => ({ ...p, issuerCnpj: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Cliente</label>
          <Input
            value={modal.recipientName}
            onChange={(e) => onChange((p) => ({ ...p, recipientName: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Documento cliente</label>
          <Input
            value={modal.recipientDocument}
            onChange={(e) => onChange((p) => ({ ...p, recipientDocument: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Total produtos</label>
          <Input
            type="number"
            step="any"
            value={modal.totalProducts}
            onChange={(e) => onChange((p) => ({ ...p, totalProducts: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Frete</label>
          <Input
            type="number"
            step="any"
            value={modal.totalFreight}
            onChange={(e) => onChange((p) => ({ ...p, totalFreight: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Desconto</label>
          <Input
            type="number"
            step="any"
            value={modal.totalDiscount}
            onChange={(e) => onChange((p) => ({ ...p, totalDiscount: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Total</label>
          <Input
            type="number"
            step="any"
            value={modal.totalValue}
            onChange={(e) => onChange((p) => ({ ...p, totalValue: e.target.value }))}
          />
        </div>
        {hasAdmNotaDownloadKey(modal.fileNameStorage, modal.fileName) && onDownload && (
          <div className="md:col-span-2">
            <button
              type="button"
              onClick={onDownload}
              disabled={downloadBusy}
              className="inline-flex shrink-0 items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Baixar anexo
            </button>
            {downloadError && <p className="text-sm text-red-600 dark:text-red-400 mt-2">{downloadError}</p>}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Data pagamento</label>
          <Input
            type="date"
            value={modal.paymentDate}
            onChange={(e) => onChange((p) => ({ ...p, paymentDate: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Observações manuais</label>
          <textarea
            rows={3}
            value={modal.manualNotes}
            onChange={(e) => onChange((p) => ({ ...p, manualNotes: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
          />
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button variant="solid" onClick={onSave} disabled={submitting}>
          {submitting ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </div>
  </div>
)

export default AdmDespesasBudgetEditModal
